import store from "store";
import { formatPrice } from 'public/Utilities/common';
import { DEFAULT_PROGRESS_BAR, CART_DETAILS, SHIPPING_LIMITS } from 'public/Utilities/misc.js';
import { controllElementVisibility } from 'public/Utilities/common.js';
import { viewMode } from 'wix-window-frontend';

export async function setupUI(props) {
    // Get dropdownValue
    const dropdownValue = props?.progressBarDesignDropdownValue || "none";

    // Get shippingLimits or use default if widgetStatus is false in Editor mode
    let shippingLimits = {
        active: props.showHideWidgetToggleValue,
        limit: props.progressBarMinCartValue,
    };

    let generalProductDetails = store.get('generalProduct');

    if (viewMode === 'Editor' && !shippingLimits?.active) {
        shippingLimits = shippingLimits || SHIPPING_LIMITS;
    }

    store.set('shippingLimits', shippingLimits);
    const widgetStatus = shippingLimits?.active ? true : false;

    // Get cartDetails or use default if widgetStatus is false in Editor mode
    let cartDetails = store.get('cartDetails');

    if (viewMode === 'Editor') {
        cartDetails = cartDetails || CART_DETAILS;
    }

    // Safeguard cartDetails access
    const total = cartDetails?.cart?.subtotal.amount ?? 0;
    const currency = cartDetails?.currency ?? generalProductDetails.currency;
    let remainingAmount = Number(Math.max(0, (shippingLimits?.limit ?? 1000) - total).toFixed(2));

    // Progress bar calculations
    const progressBarTargetValue = Number(shippingLimits?.limit ?? DEFAULT_PROGRESS_BAR.PROGRESS_BAR_TARGET_VALUE);
    let progressBarCurrentValue = Number(total);
    let progressPercentage = (total / progressBarTargetValue) * 100;

    // Adjust UI based on dropdownValue (simulate different stages)
    if (viewMode === 'Editor') {

        switch (dropdownValue) {
        case "none":
            progressPercentage = 5;
            progressBarCurrentValue = 0;
            remainingAmount = Math.max(0, (shippingLimits?.limit ?? 1000) - progressBarCurrentValue);
            break;
        case "quarter":
            progressPercentage = 25;
            progressBarCurrentValue = progressBarTargetValue * 0.25;
            remainingAmount = Math.max(0, (shippingLimits?.limit ?? 1000) - progressBarCurrentValue);
            break;
        case "half":
            progressPercentage = 50;
            progressBarCurrentValue = progressBarTargetValue * 0.50;
            remainingAmount = Math.max(0, (shippingLimits?.limit ?? 1000) - progressBarCurrentValue);
            break;
        case "threeQuarter":
            progressPercentage = 75;
            progressBarCurrentValue = progressBarTargetValue * 0.75;
            remainingAmount = Math.max(0, (shippingLimits?.limit ?? 1000) - progressBarCurrentValue);
            break;
        case "full":
            progressPercentage = 100;
            progressBarCurrentValue = progressBarTargetValue;
            remainingAmount = Math.max(0, (shippingLimits?.limit ?? 1000) - progressBarCurrentValue);
            break;
        default:
            // Keep current progressPercentage and currentValue
            break;
        }
    }

    const progressBarDiscountTextValue = `${shippingLimits?.limit ?? DEFAULT_PROGRESS_BAR.PROGRESS_BAR_DISCOUNT_VALUE}%`;
    const progressBarTextValue = `${formatPrice(currency, progressBarCurrentValue)} / ${formatPrice(currency, progressBarTargetValue)}`;

    // Setup the progress bar and progress details
    if (viewMode === 'Editor') {
        if (widgetStatus) {
            setInfoBannerState(true);
            $w('#ProgressBarElement').targetValue = Number(progressBarTargetValue);
            $w('#ProgressBarElement').value = Number(progressBarCurrentValue);
            $w('#ProgressBarDetailsLeftTextElement').text = progressBarTextValue;
            $w('#ProgressBarDetailsRightTextElement').text = progressBarDiscountTextValue;
            await setMainTextElementWithStage(progressPercentage, props, remainingAmount, currency);
            await controllElementVisibility('#widgetMainContainer', 'show');
        } else {
            setInfoBannerState(false);
            $w('#ProgressBarElement').targetValue = Number(progressBarTargetValue);
            $w('#ProgressBarElement').value = Number(progressBarCurrentValue);
            $w('#ProgressBarDetailsLeftTextElement').text = progressBarTextValue;
            $w('#ProgressBarDetailsRightTextElement').text = progressBarDiscountTextValue;
            await setMainTextElementWithStage(progressPercentage, props, remainingAmount, currency);
            await controllElementVisibility('#widgetMainContainer', 'show');
        }
    } else if (viewMode === 'Site' || viewMode === 'Preview') {
        setInfoBannerState(true);
        if (widgetStatus) {
            $w('#ProgressBarElement').targetValue = Number(progressBarTargetValue);
            $w('#ProgressBarElement').value = Number(progressBarCurrentValue);
            $w('#ProgressBarDetailsLeftTextElement').text = progressBarTextValue;
            $w('#ProgressBarDetailsRightTextElement').text = progressBarDiscountTextValue;
            await setMainTextElementWithStage(progressPercentage, props, remainingAmount, currency);
            await controllElementVisibility('#widgetMainContainer', 'show');
        } else {
            await controllElementVisibility('#widgetMainContainer', 'hide');
        }
    }
}

function setInfoBannerState(widgetStatus) {
    if (widgetStatus) {
        $w("#BannerMultiStateBox").changeState("BannerEmptyState");
    } else {
        $w("#BannerMultiStateBox").changeState("BannerState");
    }
}

async function setMainTextElementWithStage(progressPercentage, props, remainingAmount, currency) {
    // Safeguard access to cartDetails and nested properties
    let message = "";
    let successMessage = "";

    if (progressPercentage >= 100) {
        message = props.progressFullItem.progressMessage ||
            `🙌 You did it! FREE SHIPPING is now active`;
        successMessage = props.progressFullItem.successMessage;
        $w("#ProgressBarElement").style.foregroundColor = props.progressFullItem.progressBarForegroundColor;
        $w("#ProgressBarElement").style.backgroundColor = props.progressFullItem.progressBarBackgroundColor;
        $w("#ProgressMainTextElement").text = message;
        $w("#progressSuccessTextElement").text = successMessage;
        $w("#progressSuccessContainer").style.backgroundColor = props.progressFullItem.successMessageContainerColor;
        $w("#progressSuccessContainer").style.borderColor = props.progressFullItem.successMessageBorderColor;
        let borderWidth = parseInt(props?.progressFullItem?.successMessageBorderWidth) || 1;
        $w("#progressSuccessContainer").style.borderWidth = `${borderWidth}px`;
        $w("#ProgressMainTextElement").html = `<p style="color: ${props.progressFullItem.progressMessageTextColor} !important; font-family: ${props.progressFullItem.progressMessageFont} !important;">${message}</p>`;
    } else if (progressPercentage >= 75) {
        message = props.progress3QuarterItem.progressMessage
            ?.replaceAll("{X}", formatPrice(currency, remainingAmount)) ||
            `🎉 Nearly there! Add ${formatPrice(currency, remainingAmount)} to enjoy FREE SHIPPING`;
        $w("#ProgressBarElement").style.foregroundColor = props.progress3QuarterItem.progressBarForegroundColor;
        $w("#ProgressBarElement").style.backgroundColor = props.progress3QuarterItem.progressBarBackgroundColor;
        $w("#ProgressMainTextElement").text = message;

        $w("#ProgressMainTextElement").html = `<p style="color: ${props.progress3QuarterItem.progressMessageTextColor} !important; font-family: ${props.progress3QuarterItem.progressMessageFont} !important;">${message}</p>`;
    } else if (progressPercentage >= 50) {
        message = props.progressHalfItem.progressMessage
            ?.replaceAll("{X}", formatPrice(currency, remainingAmount)) ||
            `🔥 Keep shopping — FREE SHIPPING is only ${formatPrice(currency, remainingAmount)} away.`;
        $w("#ProgressBarElement").style.foregroundColor = props.progressHalfItem.progressBarForegroundColor;
        $w("#ProgressBarElement").style.backgroundColor = props.progressHalfItem.progressBarBackgroundColor;
        $w("#ProgressMainTextElement").text = message;

        $w("#ProgressMainTextElement").html = `<p style="color: ${props.progressHalfItem.progressMessageTextColor} !important; font-family: ${props.progressHalfItem.progressMessageFont} !important;">${message}</p>`;
    } else if (progressPercentage >= 25) {
        message = props.progressQuarterItem.progressMessage
            ?.replaceAll("{X}", formatPrice(currency, remainingAmount)) ||
            `🚀 You’re making progress — just ${formatPrice(currency, remainingAmount)} more for FREE SHIPPING!`;
        $w("#ProgressBarElement").style.foregroundColor = props.progressQuarterItem.progressBarForegroundColor;
        $w("#ProgressBarElement").style.backgroundColor = props.progressQuarterItem.progressBarBackgroundColor;
        $w("#ProgressMainTextElement").text = message;

        $w("#ProgressMainTextElement").html = `<p style="color: ${props.progressQuarterItem.progressMessageTextColor} !important; font-family: ${props.progressQuarterItem.progressMessageFont} !important;">${message}</p>`;
    } else {
        message = props.progressNoneItem.progressMessage
            ?.replaceAll("{X}", formatPrice(currency, remainingAmount)) ||
            `🚚 Get FREE SHIPPING — add items worth ${formatPrice(currency, remainingAmount)} to your cart to unlock it! to your cart!`;
        $w("#ProgressBarElement").style.foregroundColor = props.progressNoneItem.progressBarForegroundColor;
        $w("#ProgressBarElement").style.backgroundColor = props.progressNoneItem.progressBarBackgroundColor;
        $w("#ProgressMainTextElement").text = message;

        $w("#ProgressMainTextElement").html = `<p style="color: ${props.progressNoneItem.progressMessageTextColor} !important; font-family: ${props.progressNoneItem.progressMessageFont} !important;">${message}</p>`;
    }

    // Update the UI elements
    controllElementVisibility('#progressSuccessContainer', progressPercentage >= 100 ? 'show' : 'hide');
}